import React from "react";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { Employee } from "../types";

const DropdownContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 16px;
  font-size: 20px;
  border-radius: 12px;
  border: none;
  background: #232323;
  color: #fff;
  cursor: pointer;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (min-width: 768px) {
    font-size: 22px;
  }
`;

const DropdownOptions = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #232323;
  border-radius: 12px;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  display: ${({ isOpen }) => (isOpen ? "block" : "none")};
`;

const DropdownOption = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  color: #fff;
  font-size: 16px;
  border-bottom: 1px solid #393939;

  &:hover {
    background: #393939;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const LoadingSpinner = styled.div`
  padding: 16px;
  text-align: center;
  color: #fff;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  padding: 16px;
  color: #ff4444;
  font-size: 14px;
  text-align: center;
`;

interface EmployeeDropdownProps {
  employees: Employee[];
  selectedEmployee: Employee | null;
  isOpen: boolean;
  isLoading?: boolean;
  error?: any;
  placeholder?: string;
  onToggle: () => void;
  onSelect: (employee: Employee) => void;
}

const EmployeeDropdown: React.FC<EmployeeDropdownProps> = ({
  employees,
  selectedEmployee,
  isOpen,
  isLoading = false,
  error,
  placeholder = "Select Employee",
  onToggle,
  onSelect,
}) => {
  const displayText = selectedEmployee
    ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}`
    : placeholder;

  if (isLoading) {
    return (
      <DropdownContainer>
        <LoadingSpinner>Loading employees...</LoadingSpinner>
      </DropdownContainer>
    );
  }

  if (error) {
    return (
      <DropdownContainer>
        <ErrorMessage>Failed to load employees</ErrorMessage>
      </DropdownContainer>
    );
  }

  return (
    <DropdownContainer>
      <DropdownButton onClick={onToggle}>
        {displayText}
        <FontAwesomeIcon icon={faCaretDown} />
      </DropdownButton>
      <DropdownOptions isOpen={isOpen}>
        {employees.map((employee) => (
          <DropdownOption key={employee.pin} onClick={() => onSelect(employee)}>
            {employee.first_name} {employee.last_name}
          </DropdownOption>
        ))}
      </DropdownOptions>
    </DropdownContainer>
  );
};

export default EmployeeDropdown;
